import SetPinComponent from "@/components/Settings/UserManagement/setPin/SetPin.component";
import { Setting, User } from "@/interfaces/user";
import { EventBus, store } from "@/internal";
import { messagesService } from "@/services/messages.service";
import { AxiosResponse } from "axios";
import Cookie from "js-cookie";
import Vue from "vue";
import HttpService from "./http.service";

export class UsersService extends HttpService {
  protected uri: string = "users";
  protected loadDispatcher: string = "UserModule/loadUsers";
  protected defaultSort: string = "name";
  protected emptyResultSet: User[] | null = null;

  constructor() {
    super();
  }

  public async get(): Promise<User[]> {
    const response = await super.get(this.query);
    return response.data.data;
  }

  public async getProfile(id: number): Promise<User[]> {
    this.query.id = id;
    const response = await super.get(this.query);
    return response.data.data[0];
  }

  public async updateSettings(id: number, settings: Setting) {
    let response;
    try {
      response = await Vue.axios({
        method: "PUT",
        url: `users/${id}/settings`,
        data: settings
      });
    } catch (e) {
      messagesService.renderErrorMessage(e);
    }

    return (response && response.data.data) || undefined;
  }

  public async generateNewPin() {
    const user = store.getters["authModule/user"];
    const pinCode = await this.$modals.load<string>(SetPinComponent, {
      size: "fit",
      positionY: "top"
    });

    if (pinCode) {
      const pin = {
        account_id: user.settings.current_account_id,
        method: "PIN_CODE",
        value: pinCode
      };

      try {
        await Vue.axios({
          method: "PUT",
          url: `users/${user.id}/account_auth_method`,
          data: pin
        });

        EventBus.$emit("notify", {
          text: "pin_generated",
          color: "primary"
        });
      } catch (e) {
        messagesService.renderErrorMessage(e);
      }
    }
  }

  public async changePassword(password: string) {
    this.uri = "users";
    const currentUser: User = {
      ...store.getters["authModule/user"],
      password
    };

    const passwordModel = {
      password: currentUser.password,
      password_confirmation: password
    };
    await Vue.axios({
      method: "PUT",
      url: `${this.uri}/${currentUser.id}/password`,
      data: passwordModel
    });
    this.uri = "users";
    store.dispatch("authModule/updateUser", currentUser);
    EventBus.$emit("notify", { text: "password_changed", color: "green" });
    // after changing password, removing the stored cookie information so that previously set password gets removed.
    Cookie.remove("app_map", {
      domain: process.env.VUE_APP_COOKIE_DOMAIN
    });
    Cookie.remove("auth_data", {
      domain: `${process.env.VUE_APP_COOKIE_DOMAIN}`
    });
    Cookie.remove("userInfo");
    Cookie.remove("referrer_app", {
      domain: `${process.env.VUE_APP_COOKIE_DOMAIN}`
    });
    // after removing cookie info, redirecting to login page.
    window.location.href = `${
      process.env.VUE_APP_OAUTH_URL
    }/logout?response_type=code&client_id=${
      process.env.VUE_APP_OAUTH_CLIENT_ID
    }&redirect_uri=${
      process.env.VUE_APP_CORE_FRONTEND
    }/oauth/callback&response_type=code&scope=${encodeURI(
      process.env.VUE_APP_OAUTH_SCOPES
    )}`;
  }

  public async post(user: FormData): Promise<any> {
    const response = await super.post(user);
    return response.data;
  }

  public async put(user: User, formData: FormData): Promise<any> {
    const response: AxiosResponse = await Vue.axios({
      method: "POST",
      url: `${this.uri}/${user.id}`,
      data: formData
    });
    return response.data;
  }

  public async searchUsers(value: string): Promise<User[]> {
    if (this.emptyResultSet && !value) {
      return this.emptyResultSet;
    }
    try {
      const users = await super.get({
        "q[first_name_or_last_name_or_nickname_or_middle_name_or_email_contains]": value
      });
      if (!value && !this.emptyResultSet) {
        this.emptyResultSet = users.data.data;
      }
      return users.data.data;
    } catch (e) {
      messagesService.renderErrorMessage(e);
      return [];
    }
  }
}

export const usersService = new UsersService();
